import axios, { AxiosInstance, AxiosResponse } from 'axios';
import queryString, { StringifyOptions } from 'query-string';
import Cookies from 'js-cookie';

interface ApiResponse<T> {
  data: T;
}

interface ApiError {
  response?: AxiosResponse;
  request?: XMLHttpRequest;
  message?: string;
}

interface ApiConfig {
  onError: (error: any) => void;
}

const QUERY_OPTIONS: StringifyOptions = { arrayFormat: 'index' };

const instance: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_END_POINT,
  timeout: 30000,
  headers: {
    Accepts: 'application/json',
    'Content-type': 'application/json',
    'Access-Control-Allow-Origin': '*',
    'Access-Control-Allow-Credentials': true,
  },
});

instance.interceptors.request.use((config: any) => {
  const token = Cookies.get('_token');

  if (token) config.headers.Authorization = `Bearer ${token}`;

  return config;
});

class Api {
  private request: AxiosInstance;
  private onError: (error: any) => void;
  private result: any;

  constructor(args: ApiConfig) {
    const { onError } = args;
    this.request = instance;
    this.onError = onError;
    this.result = {};
  }

  private getError = ({ response, request, message }: ApiError) => {
    if (response) return response;
    if (request) return request;
    if (message) return message;

    return null;
  };

  public event = async ({ url, params }: { url: string; params: any }) => {
    try {
      const token = Cookies.get('_token');
      const eventSource = new EventSource(
        `${process.env.REACT_APP_END_POINT}${url}?${queryString.stringify(
          {
            ...params,
            token,
          },
          QUERY_OPTIONS,
        )}`,
      );

      return eventSource;
    } catch (err) {
      const error = this.getError(err as ApiError);
      this.onError(error);
      throw error;
    }
  };

  public get = async <T>({
    url,
    params,
    transformer,
  }: {
    url: string;
    params?: any;
    transformer?: (data: any) => T;
  }): Promise<T> => {
    try {
      const res: AxiosResponse<ApiResponse<T>> = await this.request.get(
        `${url}?${queryString.stringify(params, QUERY_OPTIONS)}`,
      );

      return transformer ? transformer(res?.data as T) : (res?.data as T);
    } catch (err) {
      const error = this.getError(err as ApiError);
      this.onError(error);
      throw error;
    }
  };

  public post = async <T>({
    url,
    payload,
    params,
    transformer,
  }: {
    url: string;
    payload: any;
    params?: any;
    transformer?: (data: any) => T;
  }): Promise<T> => {
    try {
      const res: ApiResponse<T> = await this.request.post(
        `${url}?${queryString.stringify(params, QUERY_OPTIONS)}`,
        payload,
        {
          timeout: 0
        }
      );

      return transformer ? transformer(res?.data) : res?.data;
    } catch (err) {
      const error = this.getError(err as ApiError);
      this.onError(error);
      throw error;
    }
  };

  public patch = async <T>({
    url,
    payload,
    params,
    transformer,
  }: {
    url: string;
    payload: any;
    params?: any;
    transformer?: (data: any) => T;
  }): Promise<T> => {
    try {
      const res: AxiosResponse<ApiResponse<T>> = await this.request.patch(
        `${url}?${queryString.stringify(params, QUERY_OPTIONS)}`,
        payload,
      );

      return transformer ? transformer(res?.data.data) : res?.data.data;
    } catch (err) {
      const error = this.getError(err as ApiError);
      this.onError(error);
      throw error;
    }
  };

  public put = async <T>({
    url,
    payload,
    params,
    transformer,
  }: {
    url: string;
    payload: any;
    params?: any;
    transformer?: (data: any) => T;
  }): Promise<T> => {
    try {
      const res: AxiosResponse<ApiResponse<T>> = await this.request.put(
        `${url}?${queryString.stringify(params, QUERY_OPTIONS)}`,
        payload,
      );

      return transformer ? transformer(res?.data.data) : res?.data.data;
    } catch (err) {
      const error = this.getError(err as ApiError);
      this.onError(error);
      throw error;
    }
  };

  public delete = async <T>({
    url,
    params,
    transformer,
  }: {
    url: string;
    params?: any;
    transformer?: (data: any) => T;
  }): Promise<T> => {
    try {
      const res: AxiosResponse<ApiResponse<T>> = await this.request.delete(
        `${url}?${queryString.stringify(params, QUERY_OPTIONS)}`,
      );

      return transformer ? transformer(res?.data.data) : res?.data.data;
    } catch (err) {
      const error = this.getError(err as ApiError);
      this.onError(error);
      throw error;
    }
  };
}

export default Api;
